import React, { useState, useContext } from 'react';
import { UserContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Loader2, Croissant, UserPlus, LogIn, Globe, Check } from 'lucide-react';

const LoginStable = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'staff'
  });
  const [loading, setLoading] = useState(false);
  const { handleLogin } = useContext(UserContext);
  
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Langues simples
  const languages = {
    fr: { name: 'Français', flag: '🇫🇷' },
    en: { name: 'English', flag: '🇺🇸' },
    ar: { name: 'العربية', flag: '🇸🇦' }
  };

  const texts = {
    fr: { title: 'Suivi de Production', subtitle: 'Boulangerie - Système de Gestion', connection: 'Connexion', registration: 'Inscription', username: 'Nom d\'utilisateur', password: 'Mot de passe', confirmPassword: 'Confirmer le mot de passe', role: 'Rôle dans la boulangerie', connect: 'Se connecter', register: 'S\'inscrire', language: 'Langue' },
    en: { title: 'Production Tracking', subtitle: 'Bakery - Management System', connection: 'Login', registration: 'Register', username: 'Username', password: 'Password', confirmPassword: 'Confirm Password', role: 'Role in bakery', connect: 'Sign In', register: 'Sign Up', language: 'Language' },
    ar: { title: 'تتبع الإنتاج', subtitle: 'المخبز - نظام الإدارة', connection: 'تسجيل الدخول', registration: 'التسجيل', username: 'اسم المستخدم', password: 'كلمة المرور', confirmPassword: 'تأكيد كلمة المرور', role: 'الدور في المخبز', connect: 'تسجيل الدخول', register: 'إنشاء حساب', language: 'اللغة' }
  };

  const roles = {
    fr: { staff: 'Commis', manager: 'Gérant', owner: 'Propriétaire', system_admin: 'Administrateur du système' },
    en: { staff: 'Staff', manager: 'Manager', owner: 'Owner', system_admin: 'System Administrator' },
    ar: { staff: 'موظف', manager: 'مدير', owner: 'مالك', system_admin: 'مدير النظام' }
  };

  const t = (key) => texts[language][key] || texts.fr[key] || key;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', confirmPassword: '', role: 'staff' });
  };

  const changeLanguage = (langCode) => {
    try {
      setLanguage(langCode);
      setShowLanguageMenu(false);
      localStorage.setItem('app_language', langCode);
      
      // Appliquer la direction
      document.documentElement.dir = languages[langCode].flag === '🇸🇦' ? 'rtl' : 'ltr';
      document.documentElement.lang = langCode;
      
      toast.success(`Langue changée : ${languages[langCode].name}`);
    } catch (error) {
      console.error('Language change error:', error);
      toast.error('Erreur lors du changement de langue');
    }
  };

  const handleSignUp = async () => {
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          role: formData.role
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        
        // Message d'approbation pour tous sauf system_admin
        if (formData.role !== 'system_admin') {
          toast.info('Votre compte sera activé après approbation de l\'administrateur système.', { 
            duration: 10000 
          });
        }
        
        setIsSignUp(false);
        resetForm();
        
      } else {
        toast.error(data.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error('Le mot de passe doit contenir au moins 6 caractères');
          setLoading(false);
          return;
        }
        await handleSignUp();
      } else {
        const result = await handleLogin(formData.username, formData.password);
        if (result.success) {
          toast.success('Connexion réussie !');
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du traitement');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    resetForm();
  };

  const getRoleDisplayName = (roleValue) => {
    return roles[language][roleValue] || roleValue;
  };

  const currentLang = languages[language];
  const isRTL = currentLang.flag === '🇸🇦';

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md space-y-8">
        {/* Language Selector - Simplifié */}
        <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
          <div className="relative">
            <Button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              variant="outline"
              className="flex items-center gap-2 bg-white/70 border-gray-200 hover:bg-gray-50 transition-all duration-200"
              data-testid="language-btn"
            >
              <Globe className="h-4 w-4" />
              <span>{t('language')}</span>
              <span className="text-lg">{currentLang.flag}</span>
              <span className="font-medium">{currentLang.name}</span>
            </Button>
            
            {/* Menu des Langues */}
            {showLanguageMenu && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 min-w-56">
                <div className="px-4 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                  Choisissez votre langue / Choose language / اختر لغتك
                </div>
                {Object.entries(languages).map(([code, lang]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between transition-colors duration-200"
                    data-testid={`lang-${code}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{lang.flag}</span>
                      <div>
                        <p className="font-medium text-gray-900">{lang.name}</p>
                        <p className="text-xs text-gray-500">
                          {code === 'fr' && 'Langue par défaut'}
                          {code === 'en' && 'International language'}
                          {code === 'ar' && 'العربية الفصحى'}
                        </p>
                      </div>
                    </div>
                    {language === code && (
                      <Check className="h-5 w-5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Header */}
        <div className="text-center fade-in">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mb-6">
            <Croissant className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('title')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('subtitle')}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => !isSignUp && switchMode()}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              !isSignUp ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LogIn className="h-4 w-4" />
            {t('connection')}
          </button>
          <button
            type="button"
            onClick={() => isSignUp && switchMode()}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              isSignUp ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserPlus className="h-4 w-4" />
            {t('registration')}
          </button>
        </div>

        {/* Form */}
        <Card className="slide-up shadow-xl border-0 bg-white/70 glass">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl text-center font-semibold">
              {isSignUp ? t('registration') : t('connection')}
            </CardTitle>
            <CardDescription className="text-center text-gray-600">
              {isSignUp ? 'Créez votre espace de travail' : 'Accédez à votre espace de travail'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  {t('username')}
                </Label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="h-12 text-base border-gray-200 focus:border-orange-400"
                  placeholder={t('username')}
                  data-testid="username-input"
                  disabled={loading}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  {t('password')}
                </Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="h-12 text-base border-gray-200 focus:border-orange-400"
                  placeholder={t('password')}
                  data-testid="password-input"
                  disabled={loading}
                  required
                />
              </div>

              {isSignUp && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t('confirmPassword')}
                    </Label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="h-12 text-base border-gray-200 focus:border-orange-400"
                      placeholder={t('confirmPassword')}
                      data-testid="confirm-password-input"
                      disabled={loading}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      {t('role')}
                    </Label>
                    <select
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
                      data-testid="role-select"
                      disabled={loading}
                      required
                    >
                      <option value="staff">{getRoleDisplayName('staff')}</option>
                      <option value="manager">{getRoleDisplayName('manager')}</option>
                      <option value="owner">{getRoleDisplayName('owner')}</option>
                      <option value="system_admin">{getRoleDisplayName('system_admin')}</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Rôle sélectionné : {getRoleDisplayName(formData.role)}
                    </p>
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-medium rounded-lg shadow-lg"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isSignUp ? `${t('register')}...` : `${t('connect')}...`}
                  </>
                ) : (
                  <>
                    {isSignUp ? (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        {t('register')}
                      </>
                    ) : (
                      <>
                        <LogIn className="mr-2 h-4 w-4" />
                        {t('connect')}
                      </>
                    )}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={switchMode}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
                disabled={loading}
              >
                {isSignUp 
                  ? (language === 'ar' ? 'لديك حساب بالفعل؟ تسجيل الدخول' : 
                     language === 'en' ? 'Already have an account? Sign in' : 
                     'Vous avez déjà un compte ? Se connecter')
                  : (language === 'ar' ? 'ليس لديك حساب؟ إنشاء حساب' :
                     language === 'en' ? 'No account? Create account' :
                     'Pas de compte ? Créer un compte')
                }
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 space-y-1">
          <p>© 2025 Système de Suivi de Production Boulangerie</p>
          <p className="text-xs">
            Créé par <span className="font-medium text-gray-700">Elouho Ettien Ane Martial T.</span>
          </p>
          <p className="text-xs">Consultant Juriste-Fiscaliste & Financier</p>
          <p className="text-xs font-mono">Tél: +225 07 69 54 54 69</p>
        </div>
      </div>
    </div>
  );
};

export default LoginStable;